import './styles.scss';

import { useMutation } from '@apollo/client';
import { fromJS } from 'immutable';
import React, { useContext, useEffect, useState } from 'react';

import BuyingProcess from '../../../components/Layout/BuyingProcess';
import { CANCEL_PURCHASE } from '../../../graphql/mutations';
import { OPEN_SESSION, PURCHASE } from '../../../graphql/queries';
import { useBasketCookie, useDataLayer, useRouter } from '../../../hooks';
import useQuery from '../../../hooks/useQuery';
import { PurchaseContext } from '../../../store/Purchase';
import SummaryContent from './SummaryContent';
import SummaryError from './SummaryError';
import SummarySkeleton from './SummarySkeleton';

const PurchaseSummary = () => {
  const {
    lang,
    countryEmoIso2,
    currentRoute: { params },
  } = useRouter();
  const { getPromoCodeOrVoucher, resetBasket } = useBasketCookie();
  const promoCodeOrVoucher = getPromoCodeOrVoucher();
  const { pushEventLoadedPurchaseFunnel, pushEventLoadedCartPage } = useDataLayer();

  const { purchase, refetch: purchaseRefetch, loading: loadingPurchase } = useContext(PurchaseContext);

  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    pushEventLoadedPurchaseFunnel({
      type: 'common',
      stepName: 'cart',
      stepNumber: 1,
    });
  }, []);

  useEffect(() => {
    if (!loadingPurchase && purchase?.id) {
      pushEventLoadedCartPage({ eventCommand: 'eec_checkout', purchase: fromJS(purchase) });
    }
  }, [loadingPurchase, purchase?.id]);

  const {
    data: { openSession, ...productsAndServices },
    loading: loadingOpenSession,
  } = useQuery(OPEN_SESSION, {
    variables: {
      sessionId: purchase && purchase.openSession ? purchase.openSession.id : null,
      lang,
      country: countryEmoIso2,
      discount:
        promoCodeOrVoucher?.discountCode && !promoCodeOrVoucher?.isVoucher
          ? { discountCode: promoCodeOrVoucher.discountCode, referrer: promoCodeOrVoucher.referrer }
          : null,
    },
    skip: !purchase || !purchase.openSession,
  });
  const [cancelPurchase] = useMutation(CANCEL_PURCHASE, { variables: { registrationId: params.registrationId } });

  const handleCancel = () => {
    setCancelled(true);
    cancelPurchase({
      variables: { registrationId: params.registrationId },
      optimisticResponse: {
        __typename: 'Mutation',
        cancelPurchase: true,
      },
      update: (proxy) => {
        proxy.writeQuery({
          query: PURCHASE,
          variables: { registrationId: params.registrationId },
          data: { purchase: null },
        });
      },
      onQueryUpdated: () => {
        resetBasket();
        purchaseRefetch();
      },
    });
  };

  const loading = loadingOpenSession || loadingPurchase;

  let content;
  if (!params.registrationId) {
    content = <SummaryError translationKeyTitle="purchase-summary.purchase.empty" />;
  } else if (cancelled) {
    content = <SummaryError translationKeyTitle="session-summary.purchase.is_cancelled" />;
  } else if (loading) {
    content = <SummarySkeleton />;
  } else if (!purchase) {
    content = <SummaryError translationKeyTitle="purchase-summary.purchase.invalid" />;
  } else if (purchase.purchaseLines.length === 0) {
    content = <SummaryError translationKeyTitle="purchase-summary.purchase.empty" />;
  } else if (purchase && purchase.openSession && purchase.openSession.id) {
    if (!loading && !openSession) {
      content = <SummaryError isBannerOpenSession={true} translationKeyTitle="purchase-summary.session.not-found" />;
    } else {
      content = (
        <SummaryContent
          loading={loading}
          purchase={fromJS(purchase)}
          openSession={fromJS(openSession)}
          productsAndServices={productsAndServices}
          purchaseRefetch={purchaseRefetch}
          onCancel={handleCancel}
        />
      );
    }
  } else {
    content = (
      <SummaryContent
        loading={loading}
        purchase={fromJS(purchase)}
        purchaseRefetch={purchaseRefetch}
        onCancel={handleCancel}
      />
    );
  }

  return <BuyingProcess>{content}</BuyingProcess>;
};

export default PurchaseSummary;
